import { Link } from "react-router-dom";
import TRAIcon from "../icons/TRAIcon";
import { cn } from "../utils/cn";
import v from "./traVisual.module.css";

/** Flat player-platform page canvas (max ~480px via shell). */
export function TRAVisualPage({ className, children, ...rest }) {
  return (
    <div className={cn(v.visualPage, className)} {...rest}>
      {children}
    </div>
  );
}

export function TRAVisualSection({ className, children, as: Tag = "section", ...rest }) {
  return (
    <Tag className={cn(v.sectionBlock, className)} {...rest}>
      {children}
    </Tag>
  );
}

/** Vertical sections separated by hairline + major break (see `.sectionStack` in traVisual). */
export function TRASectionStack({
  className,
  compact = false,
  children,
  as: Tag = "div",
  ...rest
}) {
  return (
    <Tag className={cn(v.sectionStack, compact && v.sectionStackCompact, className)} {...rest}>
      {children}
    </Tag>
  );
}

export function TRAEyebrow({ as: Tag = "p", className, children, ...rest }) {
  return (
    <Tag className={cn(v.eyebrow, className)} {...rest}>
      {children}
    </Tag>
  );
}

/**
 * Section header: eyebrow title + optional trailing action (badge, link, control).
 */
export function TRASectionHeader({ id, title, action, className }) {
  return (
    <div className={cn(v.sectionHeadRow, className)}>
      <h2 id={id} className={v.eyebrow}>
        {title}
      </h2>
      {action || null}
    </div>
  );
}

export function TRACopyBody({ as: Tag = "span", className, children, ...rest }) {
  return (
    <Tag className={cn(v.copyBody, className)} {...rest}>
      {children}
    </Tag>
  );
}

export function TRACopyDetail({ as: Tag = "p", className, children, ...rest }) {
  return (
    <Tag className={cn(v.copyDetail, className)} {...rest}>
      {children}
    </Tag>
  );
}

export function TRADisplayHero({ as: Tag = "h1", className, children, ...rest }) {
  return (
    <Tag className={cn(v.displayHero, className)} {...rest}>
      {children}
    </Tag>
  );
}

export function TRADisplayTitle({ className, children, ...rest }) {
  return (
    <h2 className={cn(v.displayTitle, className)} {...rest}>
      {children}
    </h2>
  );
}

export function TRAVisualTopBar({ start, end }) {
  return (
    <header className={v.topBar}>
      {start}
      {end ?? <span className={v.topBarSpacer} aria-hidden />}
    </header>
  );
}

export function TRAIconGhostButton({ label, onClick, children }) {
  return (
    <button type="button" className={v.iconGhost} aria-label={label} onClick={onClick}>
      {children}
    </button>
  );
}

export function TRAAvatarRing({ className, children }) {
  return (
    <div className={cn(v.avatarRing, className)}>{children}</div>
  );
}

export function TRAPillButton({ variant = "outline", className, children, ...rest }) {
  const variantClass =
    variant === "cta"
      ? v.pillCta
      : variant === "primary"
        ? v.pillPrimary
        : variant === "ghost"
          ? v.pillGhost
          : v.pillOutline;
  return (
    <button type="button" className={cn(variantClass, className)} {...rest}>
      {children}
    </button>
  );
}

/** Dominant KPI (e.g. career win %). */
export function TRAKpiHero({
  eyebrow,
  value,
  suffix,
  percentWidth,
  className,
}) {
  return (
    <div className={cn(v.kpiHeroBlock, className)} aria-label={eyebrow}>
      <p className={v.eyebrow}>{eyebrow}</p>
      <div className={v.kpiHeroRow}>
        <span className={v.kpiHeroValue}>{value}</span>
        {suffix != null ? <span className={v.kpiHeroSuffix}>{suffix}</span> : null}
      </div>
      {percentWidth != null ? (
        <div className={v.kpiProgressTrack} aria-hidden>
          <div className={v.kpiProgressFill} style={{ width: `${percentWidth}%` }} />
        </div>
      ) : null}
    </div>
  );
}

export function TRAKpiStrip({ columns = 4, className, children }) {
  return (
    <div
      className={cn(v.kpiStrip, columns === 3 && v.kpiStrip3, className)}
      role="group"
    >
      {children}
    </div>
  );
}

export function TRAKpiStripCell({ label, value, accent, divider, className }) {
  return (
    <div className={cn(v.kpiStripCol, divider && v.kpiStripColRule, className)}>
      <div className={cn(v.kpiStripValue, accent && v.kpiStripValueAccent)}>{value}</div>
      <div className={v.kpiStripLabel}>{label}</div>
    </div>
  );
}

/** Three-up summary bar with horizontal rules (match history career strip). */
export function TRAKpiSummaryBar({ className, children }) {
  return (
    <div className={cn(v.kpiStripBar, className)} role="group">
      {children}
    </div>
  );
}

export function TRAKpiSummaryCell({ label, value, accent, divider, className }) {
  return (
    <div className={cn(v.kpiStripBarCol, divider && v.kpiStripColRule, className)}>
      <div className={cn(v.kpiStripValue, accent && v.kpiStripValueAccent)}>{value}</div>
      <div className={v.kpiStripLabel}>{label}</div>
    </div>
  );
}

/**
 * Settings / community list row (profile Account, Clubs, insight placeholders).
 */
export function TRASettingsRow({
  icon,
  label,
  disabled = false,
  comingSoon = false,
  comingSoonBefore = false,
  to,
  onClick,
  showChevron = false,
  className,
  ...rest
}) {
  const rowClass = cn(v.settingsRow, disabled && v.settingsRowDisabled, className);
  const soonBadge = comingSoon ? <span className={v.comingSoonBadge}>Coming soon</span> : null;
  const content = (
    <>
      <span className={v.settingsIcon}>
        <TRAIcon name={icon} size="md" />
      </span>
      {comingSoonBefore ? soonBadge : null}
      <span className={v.settingsLabel}>{label}</span>
      {!comingSoonBefore ? soonBadge : null}
      {showChevron && !comingSoon ? (
        <span className={v.settingsChevron}>
          <TRAIcon name="chevronRight" size="sm" />
        </span>
      ) : null}
    </>
  );

  if (to && !disabled) {
    return (
      <Link to={to} className={rowClass} {...rest}>
        {content}
      </Link>
    );
  }
  if (onClick && !disabled) {
    return (
      <button type="button" className={rowClass} onClick={onClick} {...rest}>
        {content}
      </button>
    );
  }
  return (
    <div className={rowClass} {...rest}>
      {content}
    </div>
  );
}

export function TRASettingsBlock({ className, children }) {
  return <div className={cn(v.settingsBlock, className)}>{children}</div>;
}

export function TRATimeline({ className, children }) {
  return <div className={cn(v.timeline, className)}>{children}</div>;
}

export function TRATimelineItem({
  text,
  when,
  active = false,
  tone = "default",
  isLast = false,
}) {
  const textClass =
    tone === "muted"
      ? cn(v.timelineText, v.timelineTextMuted)
      : tone === "soft"
        ? cn(v.timelineText, v.timelineTextSoft)
        : v.timelineText;

  return (
    <div className={v.timelineItem}>
      <div className={v.timelineRail}>
        <div className={cn(v.timelineDot, active && v.timelineDotActive)} aria-hidden />
        {!isLast ? <div className={v.timelineLine} aria-hidden /> : null}
      </div>
      <div className={cn(v.timelineRow, isLast && v.timelineRowLast)}>
        <span className={textClass}>{text}</span>
        {when ? <span className={v.whenMono}>{when}</span> : null}
      </div>
    </div>
  );
}

export function TRASignatureEmptyState({
  icon = "trophy",
  title,
  description,
  action,
}) {
  return (
    <div className={v.emptySignature} role="status">
      <div className={v.avatarRing} style={{ margin: "0 auto" }} aria-hidden>
        <TRAIcon name={icon} size="lg" />
      </div>
      <p className={v.emptyTitle}>{title}</p>
      {description ? <p className={v.emptyDesc}>{description}</p> : null}
      {action}
    </div>
  );
}

/** Disabled insight / AI row with icon (Statistics & AI insights pattern). */
export function TRAInsightRow({ icon = "barChart", label }) {
  return <TRASettingsRow icon={icon} label={label} disabled comingSoon />;
}

export function TRACtaPanel({ title, children, className }) {
  return (
    <div className={cn(v.ctaPanel, className)}>
      {title ? <p className={v.ctaPanelTitle}>{title}</p> : null}
      {children}
    </div>
  );
}

export function TRAVisualFooter({ className, children }) {
  return <div className={cn(v.visualFooter, className)}>{children}</div>;
}

/** Player identity hero block (avatar + actions + name + meta). */
export function TRAPlayerHero({ avatar, actions, title, discipline, meta }) {
  return (
    <div className={v.heroIdentity} aria-label="Player identity">
      <div className={v.heroIdentityRow}>
        {avatar}
        {actions}
      </div>
      {title}
      {discipline}
      {meta}
    </div>
  );
}

/** Placeholder heroes — compose TRAPlayerHero / TRADisplayHero for other modules. */
export function TRAClubHero({ children, className }) {
  return <div className={cn(v.heroIdentity, className)}>{children}</div>;
}

export function TRATournamentHero({ children, className }) {
  return <div className={cn(v.heroIdentity, className)}>{children}</div>;
}

export function TRAMatchHero({ children, className }) {
  return <div className={cn(v.heroIdentity, className)}>{children}</div>;
}

export function TRADashboardHero({ children, className }) {
  return <div className={cn(v.kpiHeroBlock, className)}>{children}</div>;
}

/** Accent-bordered live / status strip (spectator match, future live surfaces). */
export function TRAStatusBanner({ className, children }) {
  return (
    <div className={cn(v.statusBanner, className)} role="status">
      <span className={v.livePulseDot} aria-hidden />
      <span className={v.statusBannerText}>{children}</span>
    </div>
  );
}

/** Three-up referee / session action row (Undo · Start · Reset). */
export function TRAActionBar({ className, children }) {
  return <div className={cn(v.actionBar, className)}>{children}</div>;
}

export function TRAActionBarButton({
  variant = "default",
  className,
  children,
  ...rest
}) {
  const variantClass =
    variant === "primary" ? v.actionBarBtnPrimary : v.actionBarBtn;
  return (
    <button type="button" className={cn(variantClass, className)} {...rest}>
      {children}
    </button>
  );
}

/** Compact inline notice (tournament auto-save, etc.). */
export function TRAInfoChip({ className, children, ...rest }) {
  return (
    <p className={cn(v.infoChip, className)} {...rest}>
      {children}
    </p>
  );
}

export { v as traVisualStyles };
